<mat-drawer-container
  class="schedule-drawer-container"
  [class.availability-fullscreen]="availabilityFullscreen"
  [hasBackdrop]="availabilityDrawerMode === 'over'"
  (backdropClick)="closeAvailability()"
>
  <mat-drawer-content>
    <div class="schedule-container">
      <div class="loading-container" *ngIf="loading || running">
        <div class="loading-panel">
          <mat-spinner diameter="34"></mat-spinner>
          <span>{{
            running ? 'Running auto-assign...' : 'Loading schedule...'
          }}</span>
        </div>
      </div>

      <div class="focused-game-banner" *ngIf="requestedGameId">
        <div>
          <span class="focused-game-label">Game ID</span>
          <code>{{ requestedGameId }}</code>
          <span
            class="focused-game-error"
            *ngIf="focusedGameErrorMessage"
          >
            {{ focusedGameErrorMessage }}
          </span>
        </div>
        <a mat-stroked-button routerLink="/schedule">
          <mat-icon>arrow_back</mat-icon>
          Back to Schedule
        </a>
      </div>

      <div class="schedule-toolbar" *ngIf="!requestedGameId">
        <div class="schedule-filter-group">
          <label for="schedule-dc-type-filter">DC Type</label>
          <select
            id="schedule-dc-type-filter"
            class="filter-control"
            [(ngModel)]="filters.dcType"
            [ngModelOptions]="{ standalone: true }"
            (change)="onFiltersChanged()"
          >
            <option value="">All</option>
            <option *ngFor="let dcType of dcTypeOptions" [value]="dcType">
              {{ dcType }}
            </option>
          </select>
        </div>

        <div class="schedule-filter-group">
          <label for="schedule-league-filter">League</label>
          <select
            id="schedule-league-filter"
            class="filter-control"
            [(ngModel)]="filters.league"
            [ngModelOptions]="{ standalone: true }"
            (change)="onFiltersChanged()"
          >
            <option value="">All</option>
            <option *ngFor="let league of leagueOptions" [value]="league">
              {{ league }}
            </option>
          </select>
        </div>

        <div class="schedule-filter-group">
          <label for="schedule-game-date-filter">Game Date</label>
          <input
            id="schedule-game-date-filter"
            class="filter-control"
            type="date"
            [(ngModel)]="filters.gameDate"
            [ngModelOptions]="{ standalone: true }"
            (change)="onFiltersChanged()"
          />
        </div>

        <div class="schedule-filter-group">
          <label for="schedule-dc-date-from-filter">DC Date</label>
          <div class="schedule-date-range-inputs">
            <input
              id="schedule-dc-date-from-filter"
              class="filter-control schedule-date-range-input"
              type="date"
              [(ngModel)]="filters.dcDateFrom"
              [ngModelOptions]="{ standalone: true }"
              (change)="onDcDateFromChanged()"
              aria-label="DC date from"
            />
            <span class="schedule-date-range-separator">to</span>
            <input
              id="schedule-dc-date-to-filter"
              class="filter-control schedule-date-range-input"
              type="date"
              [(ngModel)]="filters.dcDateTo"
              [ngModelOptions]="{ standalone: true }"
              (change)="onFiltersChanged()"
              aria-label="DC date to"
            />
          </div>
        </div>

        <div class="schedule-filter-group">
          <label for="schedule-team-leader-filter">Team Leader</label>
          <select
            id="schedule-team-leader-filter"
            class="filter-control"
            [value]="filters.teamLeaderAuthId"
            (change)="onTeamLeaderFilterChanged(readSelectValue($event))"
          >
            <option value="" [selected]="filters.teamLeaderAuthId === ''"
              >All</option
            >
            <option
              [value]="unassignedTeamLeaderFilterValue"
              [selected]="
                filters.teamLeaderAuthId === unassignedTeamLeaderFilterValue
              "
            >
              Unassigned
            </option>
            <option
              *ngFor="let teamLeader of teamLeaders"
              [value]="teamLeader.auth0Id"
              [selected]="filters.teamLeaderAuthId === teamLeader.auth0Id"
            >
              {{ teamLeader.name }}
            </option>
            <option
              *ngFor="let teamLeader of inactiveAssignedTeamLeaders"
              [value]="teamLeader.auth0Id"
              [selected]="filters.teamLeaderAuthId === teamLeader.auth0Id"
            >
              {{ teamLeader.name }}
            </option>
          </select>
        </div>

        <div class="schedule-filter-group game-status-filter-label">
          <label for="schedule-game-status-filter">Game Status</label>
          <select
            id="schedule-game-status-filter"
            class="filter-control game-status-filter-select"
            [(ngModel)]="filters.gameStatus"
            [ngModelOptions]="{ standalone: true }"
            (change)="onFiltersChanged()"
          >
            <option value="">All</option>
            <option *ngFor="let status of gameStatusOptions" [value]="status">
              {{ getGameStatusLabel(status) }}
            </option>
          </select>
        </div>

        <button
          mat-button
          type="button"
          class="clear-button"
          (click)="clearFilters()"
        >
          <mat-icon>close</mat-icon>
          Clear
        </button>

        <div class="schedule-actions">
          <button
            mat-stroked-button
            type="button"
            class="availability-button"
            (click)="openAvailability()"
          >
            <mat-icon>schedule</mat-icon>
            Availability
          </button>
          <button
            mat-stroked-button
            type="button"
            routerLink="/schedule/config"
          >
            <mat-icon>settings</mat-icon>
            Configs
          </button>
          <button
            mat-raised-button
            type="button"
            class="assign-team-leaders-button"
            (click)="openAssignTeamLeaders()"
            [disabled]="
              !hasWritePermission || loading || running || totalCount === 0
            "
          >
            <mat-icon>group_add</mat-icon>
            Assign Team Leaders
          </button>
          <button
            mat-raised-button
            color="primary"
            type="button"
            (click)="runAutoAssign()"
            [disabled]="
              loading ||
              running ||
              (selectedCount === 0 && totalCount === 0) ||
              !hasWritePermission
            "
          >
            <mat-icon>play_arrow</mat-icon>
            Run AA{{ selectedCount > 0 ? ' (' + selectedCount + ')' : '' }}
          </button>
        </div>
      </div>

      <div class="schedule-table-scroll">
        <table
          mat-table
          [dataSource]="rows"
          [trackBy]="trackByGameId"
          multiTemplateDataRows
          class="schedule-table mat-elevation-z2"
        >
          <ng-container matColumnDef="select">
            <th mat-header-cell *matHeaderCellDef>
              <mat-checkbox
                [checked]="allFilteredSelected"
                (change)="toggleAllSelected()"
                aria-label="Select all games on this page"
              ></mat-checkbox>
            </th>
            <td mat-cell *matCellDef="let row">
              <mat-checkbox
                [checked]="isSelected(row)"
                (change)="toggleSelected(row)"
                [attr.aria-label]="
                  'Select ' + row.homeTeam + ' vs ' + row.awayTeam
                "
              ></mat-checkbox>
            </td>
          </ng-container>

          <ng-container matColumnDef="collectionStatus">
            <th mat-header-cell *matHeaderCellDef>Status</th>
            <td mat-cell *matCellDef="let row">
              <div class="composite-status">
                <app-game-collection-status
                  [status]="row.collectionStatus"
                ></app-game-collection-status>
                <span
                  class="status-indicator"
                  [class.status-indicator-ready]="row.hasVideo"
                  [class.status-indicator-missing]="!row.hasVideo"
                  [matTooltip]="
                    row.hasVideo ? 'Video available' : 'Video missing'
                  "
                >
                  <mat-icon>{{
                    row.hasVideo ? 'videocam' : 'videocam_off'
                  }}</mat-icon>
                  Video {{ row.hasVideo ? 'ready' : 'missing' }}
                </span>
                <span
                  class="status-indicator"
                  [class.status-indicator-ready]="row.homeLineup.ready"
                  [class.status-indicator-missing]="!row.homeLineup.ready"
                  [matTooltip]="
                    'Home lineup: ' +
                    row.homeLineup.filled +
                    '/' +
                    row.homeLineup.total
                  "
                >
                  <mat-icon>{{
                    row.homeLineup.ready ? 'check_circle' : 'cancel'
                  }}</mat-icon>
                  Home {{ row.homeLineup.filled }}/{{ row.homeLineup.total }}
                </span>
                <span
                  class="status-indicator"
                  [class.status-indicator-ready]="row.awayLineup.ready"
                  [class.status-indicator-missing]="!row.awayLineup.ready"
                  [matTooltip]="
                    'Away lineup: ' +
                    row.awayLineup.filled +
                    '/' +
                    row.awayLineup.total
                  "
                >
                  <mat-icon>{{
                    row.awayLineup.ready ? 'check_circle' : 'cancel'
                  }}</mat-icon>
                  Away {{ row.awayLineup.filled }}/{{ row.awayLineup.total }}
                </span>
              </div>
            </td>
          </ng-container>

          <ng-container matColumnDef="progress">
            <th mat-header-cell *matHeaderCellDef>Progress</th>
            <td mat-cell *matCellDef="let row">
              <div
                class="table-progress-summary"
                tabindex="0"
                [matTooltip]="progressTooltip(row)"
                matTooltipClass="schedule-progress-tooltip"
                matTooltipPosition="above"
                [matTooltipShowDelay]="200"
                [attr.aria-label]="progressTooltip(row)"
              >
                <div class="table-progress-heading">
                  <div class="table-progress-value"
                    >{{ row.progressPercentage }}%</div
                  >
                  <mat-icon aria-hidden="true">info_outline</mat-icon>
                </div>
                <div
                  class="table-progress-track"
                  role="progressbar"
                  aria-label="Overall weighted progress"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  [attr.aria-valuenow]="row.progressPercentage"
                >
                  <span [style.width.%]="row.progressPercentage"></span>
                </div>
              </div>
            </td>
          </ng-container>

          <ng-container matColumnDef="dcType">
            <th mat-header-cell *matHeaderCellDef>DC Type</th>
            <td mat-cell *matCellDef="let row">
              <span class="dc-type-badge dc-type-{{ row.dcType | lowercase }}">
                {{ row.dcType }}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="league">
            <th mat-header-cell *matHeaderCellDef>League</th>
            <td mat-cell *matCellDef="let row">{{ row.league ?? '-' }}</td>
          </ng-container>

          <ng-container matColumnDef="gameDate">
            <th mat-header-cell *matHeaderCellDef>Game Date + Time</th>
            <td mat-cell *matCellDef="let row" class="game-datetime-cell">
              <ng-container *ngIf="row.gameDateTime; else missingGameDate">
                {{
                  row.gameDateTime
                    | date: 'yyyy-MM-dd HH:mm' : row.venueUTCOffset || '+0000'
                }}
              </ng-container>
              <ng-template #missingGameDate>-</ng-template>
            </td>
          </ng-container>

          <ng-container matColumnDef="dcDatetime">
            <th mat-header-cell *matHeaderCellDef>DC Datetime</th>
            <td mat-cell *matCellDef="let row">
              {{ formatDateTime(row.dcDate, row.dcTime) }}
            </td>
          </ng-container>

          <ng-container matColumnDef="etcDatetime">
            <th mat-header-cell *matHeaderCellDef>ETC Datetime</th>
            <td mat-cell *matCellDef="let row">
              {{ formatDateTime(row.etcDate, row.etcTime) }}
            </td>
          </ng-container>

          <ng-container matColumnDef="teams">
            <th mat-header-cell *matHeaderCellDef>Home vs Away</th>
            <td mat-cell *matCellDef="let row">
              <a
                class="game-link"
                [routerLink]="['/games', row.gameId]"
                target="_blank"
                rel="noopener noreferrer"
                [matTooltip]="row.gameId"
              >
                {{ row.homeTeam }} vs {{ row.awayTeam }}
              </a>
            </td>
          </ng-container>

          <ng-container matColumnDef="teamLeader">
            <th mat-header-cell *matHeaderCellDef>Team Leader</th>
            <td mat-cell *matCellDef="let row">
              <select
                class="team-leader-select"
                [value]="row.teamLeaderAuthId ?? ''"
                (change)="onTeamLeaderChanged(row, readSelectValue($event))"
                [disabled]="
                  !hasWritePermission || running || isSavingTeamLeader(row)
                "
                [attr.aria-label]="'Team Leader for ' + row.gameId"
              >
                <option
                  value=""
                  [selected]="(row.teamLeaderAuthId ?? '') === ''"
                >
                  Unassigned
                </option>
                <option
                  *ngIf="
                    row.teamLeaderAuthId &&
                    !isActiveTeamLeader(row.teamLeaderAuthId)
                  "
                  [value]="row.teamLeaderAuthId"
                  [selected]="true"
                >
                  {{ getTeamLeaderName(row.teamLeaderAuthId) }}
                </option>
                <option
                  *ngFor="let teamLeader of teamLeaders"
                  [value]="teamLeader.auth0Id"
                  [selected]="row.teamLeaderAuthId === teamLeader.auth0Id"
                >
                  {{ teamLeader.name }}
                </option>
              </select>
            </td>
          </ng-container>

          <ng-container matColumnDef="autoAssignStatus">
            <th mat-header-cell *matHeaderCellDef>Auto Assign</th>
            <td mat-cell *matCellDef="let row">
              <span
                class="status-pill"
                [ngClass]="getAutoAssignStatusClass(row.autoAssignStatus)"
              >
                {{ row.autoAssignStatus }}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="roster">
            <th mat-header-cell *matHeaderCellDef>DC Roster</th>
            <td mat-cell *matCellDef="let row">
              <span
                class="roster-count"
                [class.roster-count-complete]="
                  row.rosterTotal > 0 && row.rosterAssigned === row.rosterTotal
                "
                [class.roster-count-incomplete]="
                  row.rosterTotal === 0 ||
                  row.rosterAssigned !== row.rosterTotal
                "
              >
                {{ row.rosterAssigned }}/{{ row.rosterTotal }}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="expand">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let row">
              <button
                mat-icon-button
                type="button"
                class="expand-toggle"
                (click)="toggleExpanded(row)"
                [attr.aria-label]="
                  isExpanded(row) ? 'Collapse roster' : 'Expand roster'
                "
              >
                <mat-icon>{{
                  isExpanded(row) ? 'keyboard_arrow_down' : 'chevron_right'
                }}</mat-icon>
              </button>
            </td>
          </ng-container>

          <ng-container matColumnDef="expandedDetail">
            <td
              mat-cell
              *matCellDef="let row"
              [attr.colspan]="displayedColumns.length"
              class="detail-cell"
            >
              <app-schedule-roster-details
                *ngIf="isExpanded(row)"
                [row]="row"
                [hasWritePermission]="hasWritePermission"
                [canMarkAssigned]="canMarkAsAssigned(row)"
                [markingAssigned]="isMarkingAssigned(row)"
                (detailsChange)="onScheduleDetailsChanged(row, $event)"
                (rosterDeleted)="onRosterDeleted($event)"
                (markAssigned)="markAsAssigned(row)"
              ></app-schedule-roster-details>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr
            mat-row
            *matRowDef="
              let row;
              let dataIndex = dataIndex;
              columns: displayedColumns
            "
            class="schedule-row hoverable-table-row"
            [class.alt-schedule-row]="dataIndex % 2 === 1"
            [class.selected-schedule-row]="isSelected(row)"
          ></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: detailRowColumns"
            class="detail-row"
            [class.hidden-detail-row]="!isExpanded(row)"
          ></tr>

          <tr class="mat-row" *matNoDataRow>
            <td
              class="mat-cell empty-row"
              [attr.colspan]="displayedColumns.length"
            >
              {{
                focusedGameErrorMessage || 'No games match your filters.'
              }}
            </td>
          </tr>
        </table>
      </div>

      <div class="schedule-footer" *ngIf="!requestedGameId">
        <mat-paginator
          [length]="totalCount"
          [pageSize]="pageSize"
          [pageIndex]="pageIndex"
          [pageSizeOptions]="pageSizeOptions"
          (page)="onPageChanged($event)"
        ></mat-paginator>
      </div>
    </div>
  </mat-drawer-content>
  <mat-drawer
    position="end"
    [mode]="availabilityDrawerMode"
    [opened]="availabilityOpened"
    (openedChange)="availabilityOpened = $event"
    [disableClose]="true"
    class="availability-drawer"
  >
    <app-availability-overview-panel
      *ngIf="availabilityActivated"
      [active]="availabilityOpened"
      [fullscreen]="availabilityFullscreen"
      (close)="closeAvailability()"
      (fullscreenChange)="onAvailabilityFullscreenChange($event)"
    ></app-availability-overview-panel>
  </mat-drawer>
</mat-drawer-container>
